import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import StaticTable from '../../../../../components/common/table/static-table';
import { useTranslation } from 'react-i18next';
import FieldTypes from '../../../../../enums/field-types';
import { TableColumn } from '../../../../../components/common/table/table';
import { DecimalInputComponent } from '../../../../../components/form/elements/decimal';
import { SelectComponent } from '../../../../../components/form/elements/select';
import { useService } from '../../../../../services/services';
import { map, switchMap, toArray } from 'rxjs/operators';
import { FormComponent } from '../../../../../components/form';
import { useFormContext, useWatch } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import Paginate from '../../../../../interfaces/paginate';
import ClassifierValue from '../../../../../interfaces/classifierValue';
import { FormMode } from '../../../../../enums/form-mode';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { LIVESTOCK_ANIMALS_WEIGHING_CREATE } from '../../../../../router/routes';
import { route } from '../../../../../services/utils';
import { forkJoin, range } from 'rxjs';
import { weighingsAndAnimalsInitialData } from './use-weighing-state';
import { useQuery } from '../../../../../hooks/use-query';
import AnimalExportButton from '../animal-export-button';
import Animal from '../../../../../interfaces/animal';

interface WeighingTableRow {
  animalId: React.ReactNode | number;
  animal_id: string;
  name: string;
  breed_code: string;
  age: string;
  births_weight: string;

  weight_type: React.ReactNode | number;
  weight: React.ReactNode | number;
  breast_circumference: React.ReactNode | number;
  neck_height: React.ReactNode | number;
  lower_back_height: React.ReactNode | number;
}

interface Props {}

export default function CustomTable(): FormComponent {
  return ({ ...rest }) => <CustomTableComponent {...rest} />;
}

const CustomTableComponent: FC<Props> = () => {
  const { getValues, setValue } = useFormContext();
  const { animalService } = useService();
  const { farmId, mode, id } = useParams<{
    farmId: string;
    mode: string;
    id: string;
  }>();
  const history = useHistory();
  const [weighingsAndAnimals, setWeighingsAndAnimals] = useState<Animal[]>(
    getValues('weighingsAndAnimals') ? getValues('weighingsAndAnimals') : []
  );

  const removeRow = useCallback(
    (elAnimalEventAnimal) => {
      const weighingsAndAnimalsData = getValues('weighingsAndAnimals') || [];

      const animals = weighingsAndAnimalsData
        .filter((n) => n.animalId !== elAnimalEventAnimal.animalId)
        .map((item) => item.animalId);

      const updateAnimals = () => {
        if (!id) {
          history.push(
            route(
              LIVESTOCK_ANIMALS_WEIGHING_CREATE.concat('?animals=:animals'),
              {
                farmId,
                animals: animals,
              }
            )
          );
        }

        const changedData = weighingsAndAnimalsData.filter(
          (item) => item.animalId !== elAnimalEventAnimal.animalId
        );

        setValue('weighingsAndAnimals', changedData);
        setWeighingsAndAnimals(changedData);
      };

      updateAnimals();
    },
    [farmId, getValues, history, id, setValue]
  );

  useEffect(() => {
    setWeighingsAndAnimals(
      getValues('weighingsAndAnimals') ? getValues('weighingsAndAnimals') : []
    );
  }, [getValues]);

  const { t } = useTranslation();

  const tp = useCallback(
    (key: string, params?: any) =>
      t(
        'pages.livestock.animals_and_events.events.weighing.'.concat(key),
        params
      ),
    [t]
  );

  const columns: TableColumn<[]>[] = useMemo(
    () => [
      { accessor: 'animalId', header: '', className: 'animal_id' },
      {
        accessor: 'animal_id',
        header: tp('animal_id'),
        className: 'animal_id',
      },
      { accessor: 'name', header: tp('name'), className: 'name' },
      { accessor: 'breed_code', header: tp('breed_code'), className: 'name' },
      {
        accessor: 'age_days_of_weighing',
        header: tp('dayOfWeighing'),
        className: 'name',
      },
      {
        accessor: 'births_weight',
        header: tp('weight_code'),
        className: 'births_weight',
      },
      {
        accessor: 'weight_type',
        header: tp('weight_type'),
        dataType: FieldTypes.DECIMAL,
        className: 'weight_type',
      },
      {
        accessor: 'weight',
        header: tp('weight'),
        dataType: FieldTypes.DECIMAL,
      },
      {
        accessor: 'breast_circumference',
        header: tp('breast_circumference'),
        dataType: FieldTypes.DECIMAL,
      },
      {
        accessor: 'neck_height',
        header: tp('neck_height'),
        dataType: FieldTypes.DECIMAL,
      },
      {
        accessor: 'lower_back_height',
        header: tp('lower_back_height'),
        dataType: FieldTypes.DECIMAL,
      },
    ],
    [tp]
  );

  function calculateAnimalAge(birthDateStr, otherDateStr) {
    let birthDate = new Date(birthDateStr);
    let otherDate = new Date(otherDateStr);

    let years = otherDate.getFullYear() - birthDate.getFullYear();
    let months = otherDate.getMonth() - birthDate.getMonth();
    let days = otherDate.getDate() - birthDate.getDate();

    if (days < 0) {
      months--;
      days += new Date(
        otherDate.getFullYear(),
        otherDate.getMonth(),
        0
      ).getDate();
    }
    if (months < 0) {
      years--;
      months += 12;
    }

    return tp('age', {
      year: years,
      monthes: months,
      days: days,
    });
  }

  const [params] = useQuery();

  useEffect(() => {
    if (!id) {
      const animalIds: string[] | null = params['animals']
        ? ([...new Set(params['animals'].split(','))] as string[])
        : null;

      if (animalIds) {
        const subscription = range(0, animalIds.length)
          .pipe(
            map((index) =>
              animalService.syncAnimalWeighingData(farmId, animalIds[index])
            ),
            toArray(),
            switchMap((array) => forkJoin(array))
          )
          .subscribe((response: any) => {
            const weighingsAndAnimalsData = response.map((item) => {
              const births_weight = item.animal_weighing.find(
                (el) => el.svaraKods === 'D'
              );
              const data = {
                ...weighingsAndAnimalsInitialData,
                animalId: item.animal.id,
                animal_id: item.animal.animal_id,
                name: item.animal.name,
                breed_code: item.animal.breed_code,
                birth_date: item.animal.birth_date,
                age_days_of_weighing:
                  item.animal.birth_date && getValues('date') !== ''
                    ? calculateAnimalAge(
                        item.animal.birth_date,
                        getValues('date')
                      )
                    : '',
                births_weight: births_weight?.dzivsvars
                  ? String(births_weight.dzivsvars)
                  : '',
              };

              return data;
            });

            setValue('weighingsAndAnimals', weighingsAndAnimalsData);
          });
        return () => subscription.unsubscribe();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestData = useCallback(() => {
    const weighingsAndAnimalsData = getValues('weighingsAndAnimals')
      ? getValues('weighingsAndAnimals')
      : weighingsAndAnimals;

    // Return early if there's no data
    if (weighingsAndAnimalsData.length === 0) {
      return { rows: [] };
    }

    // Memoize getName function
    const createGetName = (idx: number) => (str: string) =>
      `weighingsAndAnimals.${idx}.${str}`;

    const transformData: WeighingTableRow[] = weighingsAndAnimalsData.map(
      (animal, idx) => {
        const getName = createGetName(idx);

        return {
          animalId: mode !== FormMode.view && (
            <button
              onClick={(e) => {
                e.preventDefault();
                removeRow(animal);
              }}
              aria-label={`Remove row for ${animal.name || 'animal'}`} // Accessibility improvement
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          ),
          animal_id: animal.animal_id || '',
          name: animal.name || '',
          breed_code: animal.breed_code || '',
          age_days_of_weighing: animal.age_days_of_weighing || '',
          births_weight: animal.births_weight || '',
          weight_type: (
            <FeedingstuffLosses
              inputKey="select"
              name="weight_type"
              getName={getName}
            />
          ),
          weight: (
            <FeedingstuffLosses
              inputKey="decimal"
              name="weight"
              getName={getName}
            />
          ),
          breast_circumference: (
            <FeedingstuffLosses
              inputKey="decimal"
              name="breast_circumference"
              getName={getName}
            />
          ),
          neck_height: (
            <FeedingstuffLosses
              inputKey="decimal"
              name="neck_height"
              getName={getName}
            />
          ),
          lower_back_height: (
            <FeedingstuffLosses
              inputKey="decimal"
              name="lower_back_height"
              getName={getName}
            />
          ),
        };
      }
    );

    return { rows: transformData };
  }, [getValues, mode, removeRow, weighingsAndAnimals]);

  const animlsLength = getValues('weighingsAndAnimals')
    ? getValues('weighingsAndAnimals').length
    : 0;

  return (
    <>
      <AnimalExportButton animals={weighingsAndAnimals}/>
      <p className="selected_animals">
        {t('pages.livestock.animals_and_events.events.table_title', {
          count: animlsLength,
        })}
      </p>

      <StaticTable<any>
        data={requestData()}
        keyAccessor="animalId"
        columns={columns}
        aggregateLabels={{ total: tp('total') }}
        visible={true}
      />
    </>
  );
};

export function FeedingstuffLosses({
  name,
  inputKey,
  disabled = false,
  getName,
}: {
  name: string;
  inputKey: 'select' | 'decimal';
  disabled?: boolean;
  getName: (a: string) => string;
}) {
  const { dealService } = useService();
  const value = useWatch({ name });
  const { farmId, mode } = useParams<{ farmId: string; mode: string }>();
  const isDisabled = mode === FormMode.view || disabled;

  const fetchClassifierValues = useCallback(
    (_value, _loadedOptions, { page }) => {
      return dealService
        .classifierValues(farmId, {
          page,
          classifier_key: 'weight_types',
          value,
        })
        .pipe(
          map((response: Paginate<ClassifierValue>) => ({
            hasMore: response.last_page > response.current_page,
            options: response.data.map((classifier) => ({
              label: classifier.value,
              value: classifier.id,
            })),
            additional: { page: response.current_page + 1 },
          }))
        );
    },
    [dealService, farmId, value]
  );

  const asyncOptions = useMemo(
    () => fetchClassifierValues,
    [fetchClassifierValues]
  );

  const renderComponent = useMemo(() => {
    if (inputKey === 'select') {
      return (
        <SelectComponent
          disabled={isDisabled}
          name={getName(name)}
          label={null}
          column={true}
          asyncOptions={asyncOptions}
          clearable={false}
          className="select"
        />
      );
    } else {
      return (
        <DecimalInputComponent
          name={getName(name)}
          negative={false}
          disabled={isDisabled}
        />
      );
    }
  }, [inputKey, isDisabled, getName, name, asyncOptions]);

  return renderComponent;
}
